import { useTranslation } from 'next-i18next'
import { useRef, useState } from 'react'
import { v4 as uuid } from 'uuid'

import ActivityWrapper from '~/bootcamp/Activities/components/ActivityWrapper'
import Divider from '~/bootcamp/Activities/components/Divider'
import Equals from '~/bootcamp/Activities/components/Equals'
import Input from '~/bootcamp/Activities/components/Input'
import Times from '~/bootcamp/Activities/components/Times'
import { hocOnEnterPress } from '~/bootcamp/Activities/utils/activities.utils'

import FractionsActivityWrapper from '~/bootcamp/Activities/Fractions/components/FractionsActivityWrapper'
import useFocus from '~/bootcamp/Activities/hooks/useFocus'
import { ActivityProps } from '~/bootcamp/Activities/types/activities.types'
import { useEmitInstance } from '~/bootcamp/Activities/utils/instance.utils'

import useFractionExpand from './useFractionExpand'

function FractionExpand({
  answerState,
  actionButtonsFC,
  autofocus = true,
  difficultyLevel,
  onAnswerStateChange,
  onInstanceChange,
  onNextQuestionClick,
}: ActivityProps): React.ReactElement {
  const { t } = useTranslation('generics')
  const {
    question: { numerator, denominator, factor },
    answer,
    formValid,
    error,
    onInputChange,
    handleCheckAnswer,
    goToNextQuestion,
  } = useFractionExpand({
    difficultyLevel,
    onAnswerStateChange,
    onNextQuestionClick,
  })
  const [inputKey, setInputKey] = useState<string>(uuid())

  const inputRef = useRef<HTMLInputElement>(null)
  useFocus(autofocus, answerState, inputRef)

  useEmitInstance(
    onInstanceChange,
    denominator
      ? `\\frac{${numerator} \\cdot ${factor}}{${denominator} \\cdot ${factor}} = \\frac{\\square}{\\square}`
      : null,
  )

  const handleGoToNextQuestion = (): void => {
    goToNextQuestion()
    setInputKey(uuid())
  }

  const onEnterPress = (): void => {
    if (formValid) {
      hocOnEnterPress(answerState, handleCheckAnswer, handleGoToNextQuestion)
    }
  }

  return (
    <ActivityWrapper>
      <FractionsActivityWrapper>
        <div className="flex items-center space-x-1">
          <div className="flex flex-col items-center">
            <span className="flex items-center w-full justify-center space-x-1 ">
              <span className="text-2xl">{numerator}</span>
              <Times />
              <span className="text-2xl">{factor}</span>
            </span>
            <Divider />
            <span className="flex items-center w-full justify-center space-x-1">
              <span className="text-2xl">{denominator}</span>
              <Times />
              <span className="text-2xl">{factor}</span>
            </span>
          </div>
          <Equals />
          <div className="flex flex-col items-center">
            <Input
              aria-label={t('ariaLabelButtons.insertNumerator')}
              autoFocus={autofocus}
              isError={error && error.numerator}
              key={`${inputKey}-first`}
              maxDigits={3}
              onChange={(value) => onInputChange(true, value)}
              onEnter={onEnterPress}
              ref={inputRef}
              value={`${answer.numerator || ''}`}
            />
            <Divider className="my-2" />
            <Input
              aria-label={t('ariaLabelButtons.insertDenominator')}
              isError={error && error.denominator}
              key={`${inputKey}-second`}
              maxDigits={3}
              onChange={(value) => onInputChange(false, value)}
              onEnter={onEnterPress}
              value={`${answer.denominator || ''}`}
            />
          </div>
        </div>
      </FractionsActivityWrapper>
      {actionButtonsFC &&
        actionButtonsFC({
          formValid,
          correctAnswer: answerState ?? false,
          submitAnswerAction: handleCheckAnswer,
          clearFormAction: () => onInputChange(),
          goToNextQuestionAction: handleGoToNextQuestion,
        })}
    </ActivityWrapper>
  )
}

export default FractionExpand
